import { useState, useEffect } from "react";
import moment from "moment";

export const TOPUP_WINDOW_MS = 10 * 60 * 1000;
export const REPORT_DELAY_MS = 2 * 60 * 1000;

export function msToMMSS(ms: number) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

interface TopupCountdownProps {
  createdAt: string;
  transactionId?: string | number;
  amount?: number;
  currency?: string;
}

const TopupCountdown = ({
  createdAt,
  transactionId,
  amount,
  currency,
}: TopupCountdownProps) => {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const created = new Date(createdAt).getTime();
  const elapsed = now - created;
  const phase1Remaining = TOPUP_WINDOW_MS - elapsed;
  const phase2Remaining = TOPUP_WINDOW_MS + REPORT_DELAY_MS - elapsed;

  if (phase1Remaining > 0) {
    return (
      <div className="mt-4 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-5 text-center">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-blue-500">
          Estimated credit time
        </p>
        <p className="mt-2 text-5xl font-bold tabular-nums text-blue-700">
          {msToMMSS(phase1Remaining)}
        </p>
        <p className="mt-2 text-xs text-blue-600">
          Your card will be credited within this window
        </p>
      </div>
    );
  }

  if (phase2Remaining > 0) {
    return (
      <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-5 text-center">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-red-500">
          Processing delayed
        </p>
        <p className="mt-2 text-5xl font-bold tabular-nums text-red-600">
          {msToMMSS(phase2Remaining)}
        </p>
        <p className="mt-2 text-xs text-red-600">
          Processing is taking longer than expected. Please wait before reporting.
        </p>
      </div>
    );
  }

  const date = moment(createdAt).format("DD MMM YYYY, hh:mm A");
  const text = encodeURIComponent(
    `Hi, I'd like to report a pending card top-up.\n\nTransaction ID: ${transactionId ?? "-"}\nAmount: $${amount ?? "-"} ${currency || "USD"}\nDate: ${date}\n\nIt has been over 12 minutes and my card has not been credited.`,
  );

  return (
    <div className="mt-3">
      <a
        href={`https://t.me/VaultaSupport?text=${text}`}
        target="_blank"
        rel="noopener noreferrer"
        className="flex w-full items-center justify-center rounded-xl bg-red-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-red-600"
      >
        Report Transaction
      </a>
    </div>
  );
};

export default TopupCountdown;
